import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  SxProps
} from "@mui/material";

import AnnualDeductions from "./AnnualDeduction";
import AnnualGrossTaxablIncome from "./AnnualGrossTaxablIncome";
import { getPfSsfLabel } from "../SalaryCreateEdit";
import { RetirementFundType, TRetirementFundType } from "../../../../constants/payroll";
import {
  TEarningDeductionComponent,
  TReimbursements,
  TSalarySummary
} from "../../../../interfaces/SalaryInterface";
import { rupeesDisplayFull } from "../../../../helpers/util";

export interface SalaryBreakdownProps {
  bonuses: Record<string, TEarningDeductionComponent>;
  deductions: Record<string, TEarningDeductionComponent>;
  salarySummary: TSalarySummary;
  fundType: Exclude<TRetirementFundType, typeof RetirementFundType.CITIZEN_INVESTMENT_TRUST>;
  sxProp?: SxProps;
  reimbursements?: TReimbursements;
}

const SalaryBreakdown: React.FC<SalaryBreakdownProps> = ({
  bonuses,
  deductions,
  salarySummary,
  fundType,
  sxProp,
  reimbursements = {}
}) => {
  const pfSsfLabel = getPfSsfLabel({
    pf: fundType === RetirementFundType.PROVIDENT_FUND,
    ssf: fundType === RetirementFundType.SOCIAL_SECURITY_FUND
  });

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        ...sxProp
      }}
    >
      {/* Annual Salary Breakdown */}
      <Box>
        <TableContainer
          component={Paper}
          sx={{ mt: 0.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Basic Proportionate Salary (BPS)</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(salarySummary?.salary?.basicProportionateSalary || 0)}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Total Proportionate Allowance (TPA)</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(salarySummary?.salary?.totalProportionateAllowance || 0)}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  })}
                >
                  Gross Monthly Salary
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    textAlign: "right",
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  })}
                >
                  {rupeesDisplayFull(salarySummary?.salary?.grossMonthlySalary || 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Currrent Month Benefits */}
      <Box>
        <Typography>Benefits</Typography>
        <TableContainer
          component={Paper}
          sx={{ mt: 0.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>
                  {fundType
                    ? `${pfSsfLabel} Contributions (from Employer)`
                    : "Employer Contributions"}
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(
                    salarySummary?.retirementContributions?.summary
                      ?.employerCurrentRetirementContribution || 0
                  )}
                </TableCell>
              </TableRow>

              {Object.entries(bonuses).map(([key, bonus]) =>
                bonus?.value > 0 ? (
                  <TableRow key={key}>
                    <TableCell>
                      {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                    </TableCell>
                    <TableCell sx={{ textAlign: "right" }}>
                      {rupeesDisplayFull(bonus?.value || 0)}
                    </TableCell>
                  </TableRow>
                ) : null
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Current Month Deductions */}
      <Box>
        <Typography>Deductions</Typography>
        <TableContainer
          component={Paper}
          sx={{ mt: 0.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>
                  {fundType
                    ? `${pfSsfLabel} Contributions (from Employee)`
                    : "Employee Contributions"}
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(
                    salarySummary?.retirementContributions?.summary
                      ?.employeeCurrentRetirementContribution || 0
                  )}
                </TableCell>
              </TableRow>

              {Object.entries(deductions).map(([key, deduction]) =>
                deduction?.value > 0 ? (
                  <TableRow key={key}>
                    <TableCell>
                      {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                    </TableCell>
                    <TableCell sx={{ textAlign: "right" }}>
                      {rupeesDisplayFull(deduction?.value ?? 0)}
                    </TableCell>
                  </TableRow>
                ) : null
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* annual gross taxable income */}
      <AnnualGrossTaxablIncome salarySummary={salarySummary} />

      {/* Annual Deductions */}
      <AnnualDeductions salarySummary={salarySummary} pfSsfLabel={pfSsfLabel} fundType={fundType} />

      {/* net taxable income */}
      <Box>
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableBody>
              <TableRow sx={{ fontWeight: 600, color: "#43a047" }}>
                <TableCell>Annual Net Taxable Income</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(salarySummary?.tax?.netAnnualTaxLiability)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Tax Calculations Yearly */}
      <Box>
        <Typography>Tax Calculations Yearly</Typography>
        <TableContainer
          component={Paper}
          sx={{ mt: 0.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ textAlign: "left" }}>Taxable Amount</TableCell>
                <TableCell sx={{ textAlign: "right" }}>Tax Rate (%)</TableCell>
                <TableCell sx={{ textAlign: "right" }}>Tax Liability</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salarySummary?.tax?.taxBreakdown.map((row) => (
                <TableRow key={row.slabNum}>
                  <TableCell>{rupeesDisplayFull(row.slabAmount || 0)}</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>{row.slabRate} %</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    {rupeesDisplayFull(row.slabTax || 0)}
                  </TableCell>
                </TableRow>
              ))}

              <TableRow sx={{ fontWeight: 600, color: "#43a047" }}>
                <TableCell
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  })}
                >
                  {rupeesDisplayFull(
                    salarySummary?.tax?.taxBreakdown.reduce(
                      (sum, row) => sum + row.slabAmount,
                      0
                    ) || 0
                  )}
                </TableCell>
                <TableCell aria-label="empty" />
                <TableCell
                  sx={(theme) => ({
                    textAlign: "right",
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  })}
                >
                  {rupeesDisplayFull(
                    salarySummary?.tax?.taxBreakdown.reduce((sum, row) => sum + row.slabTax, 0) || 0
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Tax waiver and rebate */}
      <Box>
        <Typography>Tax Rebate</Typography>
        <TableContainer
          component={Paper}
          sx={{ mt: 0.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ textAlign: "left" }}>Type</TableCell>
                <TableCell sx={{ textAlign: "right" }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Female 10% Rebate</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(salarySummary?.tax?.femaleRebate || 0)}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Net Tax Payable (after rebates)</TableCell>
                <TableCell sx={{ textAlign: "right", fontWeight: 600 }}>
                  {rupeesDisplayFull(salarySummary?.tax?.netCurrentMonthTax || 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <TableContainer
          component={Paper}
          sx={{ mt: 0.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Tax Adjustment</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(salarySummary?.tax?.taxAdjustment || 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Employee Pay Summary */}
      <Box>
        <Typography>Employee Pay Summary</Typography>
        <TableContainer
          component={Paper}
          sx={{
            mt: 0.5,
            boxShadow: "none",
            border: "1px solid",
            borderColor: "divider"
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ textAlign: "left" }}>Pay Summary</TableCell>
                <TableCell sx={{ textAlign: "right" }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Gross Monthly Salary</TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {rupeesDisplayFull(salarySummary?.salary?.grossMonthlySalary || 0)}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ color: "#b71c1c" }}>
                  {fundType
                    ? `${pfSsfLabel} Contributions (from Employee)`
                    : "Employee Contributions"}
                </TableCell>
                <TableCell sx={{ textAlign: "right", color: "#b71c1c" }}>
                  {rupeesDisplayFull(
                    salarySummary?.retirementContributions?.summary
                      ?.employeeCurrentRetirementContribution || 0
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ color: "#b71c1c" }}>Net Monthly Tax</TableCell>
                <TableCell sx={{ textAlign: "right", color: "#b71c1c" }}>
                  {rupeesDisplayFull(salarySummary?.tax?.netCurrentMonthTax || 0)}
                </TableCell>
              </TableRow>

              {/* --- Employee Pay Summary Table --- */}
              <TableRow>
                <TableCell
                  colSpan={1}
                  sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 600 })}
                >
                  Net Salary Monthly
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textAlign: "right"
                  })}
                >
                  {rupeesDisplayFull(salarySummary?.salary?.netMonthlySalary || 0)}
                </TableCell>
              </TableRow>

              {/* reimbursement total */}
              <TableRow>
                <TableCell
                  colSpan={1}
                  sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 600 })}
                >
                  Total Reimbursements
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textAlign: "right"
                  })}
                >
                  {rupeesDisplayFull(
                    Object.values(reimbursements).reduce(
                      (total, reimbursement) => total + reimbursement.value,
                      0
                    )
                  )}
                </TableCell>
              </TableRow>

              {/* total payable */}
              <TableRow>
                <TableCell
                  colSpan={1}
                  sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 600 })}
                >
                  Total Payable
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textAlign: "right"
                  })}
                >
                  {rupeesDisplayFull(
                    (salarySummary?.salary?.totalPayable ||
                      salarySummary?.salary?.netMonthlySalary) ??
                      0
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SalaryBreakdown;
